/* eslint-disable react-hooks/exhaustive-deps */
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from '@tanstack/react-table'
import i18next, {t} from 'i18next'
import {FC, useMemo} from 'react'
import {useQuery} from 'react-query'
import {useRoleUser} from '../../hooks/useRoleUser'
import {useAppDispatch, useAppSelector} from '../../redux/hooks'
import {filterSelector, setFilterContracts} from '../../redux/slices/filterSlices'
import {fetchContracts} from '../../services/fetcher'
import {badgeColorByStatusId, badgeColorByStatusText} from '../../utils'
import {ActionIcon} from '../ActionIcon/ActionIcon'
import {PaginationTable} from '../PaginationTable/PaginationTable'
import {SelectPageSize} from '../SelectPageSize/SelectPageSize'
import * as TableUI from '../Table/Table'
import {TableEmptyData} from '../TableEmptyData'
import {TableLoader} from '../TableLoader'
import {IOrder} from './types'
import {Filter} from './Filter'

interface Props {
  headerContent?: React.ReactNode
  onClickDetail: (order: IOrder) => void
  onClickAttachment: (order: IOrder) => void
  onClickComment: (order: IOrder) => void
}

const columnHelper = createColumnHelper<IOrder>()

export const ContractsTable: FC<Props> = (props) => {
  const {headerContent, onClickDetail, onClickAttachment, onClickComment} = props

  const {filterContracts: filter} = useAppSelector(filterSelector)
  const dispatch = useAppDispatch()
  const {roleUser} = useRoleUser()

  const {data, isLoading} = useQuery({
    keepPreviousData: true,
    queryKey: [
      'contracts',
      filter.per_page,
      filter.status,
      filter.payment_type,
      filter.grand_total,
      filter.total_quantum,
      filter.product_type,
      filter.validity_date_to,
    ],
    queryFn: () => fetchContracts(filter).then((res) => res.data),
  })

  const tableData = useMemo(() => {
    if (data) return data.data.data
  }, [data])

  const columns = useMemo(
    () => [
      columnHelper.accessor('contract_number', {
        header: () => t('kontrak-nomor'),
        cell: (info) => `12${info.getValue()}`,
      }),
      columnHelper.accessor('sap_number', {
        header: () => t('order-pelanggan'),
        cell: (info) => info.getValue() ?? '-',
      }),
      columnHelper.accessor('customer.name', {
        header: () => t('order-pelanggan'),
        cell: (info) => {
          const title = info.row.original.customer?.title
          const name = info.getValue()
          return title ? `${title} ${name}` : name
        },
      }),
      columnHelper.accessor('perihal', {
        header: () => t('order-perihal'),
        cell: (info) => {
          const value = info.getValue()
          const isPenjualan = value === 'PO001'
          const label = isPenjualan ? t('penjualan') : t('Non-penjualan')
          const badgeColor = !isPenjualan ? 'badge-light-primary' : 'badge-light-success'
          return <span className={`badge ${badgeColor}`}>{label}</span>
        },
      }),
      columnHelper.accessor('sector.name', {
        header: () => t('Sektor'),
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('anper.name', {
        header: 'Sales Org',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('sip_order.pengajuan_order.po_number', {
        header: () => t('Order Number'),
        cell: (info) => `10${info.getValue()}`,
      }),
      columnHelper.accessor('sip_order.SIP_number', {
        header: () => t('SIP Number'),
        cell: (info) => `11${info.getValue()}`,
      }),
      columnHelper.accessor('release_date', {
        header: () => t('kontrak-tanggal'),
        cell: (info) => info.getValue()?.split(' ')[0] ?? '-',
      }),
      columnHelper.accessor('validity_date_from', {
        header: () => t('Start-Date'),
        cell: (info) => info.getValue()?.split(' ')[0] ?? '-',
      }),
      columnHelper.accessor('validity_date_to', {
        header: () => t('End-Date'),
        cell: (info) => info.getValue()?.split(' ')[0] ?? '-',
      }),
      columnHelper.accessor('product_types', {
        header: () => t('sub-order-jenis'),
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('detail_quantity.total_tonnase', {
        id: 'total_quantum',
        header: () => `Total ${t('kontrak-kuantum')}`,
        cell: (info) =>
          `${info.getValue()?.toLocaleString(i18next.language, {
            maximumFractionDigits: 4,
          })} Ton`,
      }),
      columnHelper.accessor('grand_total', {
        header: () => `Total ${t('kontrak-harga')}`,
        cell: (info) => {
          const value = info.getValue()
          const maximumFractionDigits = 2
          const isUSD = info.row.original.sector_id === 3
          const currency = !isUSD ? 'Rp' : 'USD'
          return value
            ? `${currency}${value.toLocaleString(i18next.language, {maximumFractionDigits})}`
            : ''
        },
      }),
      columnHelper.accessor('payment_methods', {
        header: () => t('Metode Pembayaran'),
        cell: (info) => info.getValue() ?? '-',
      }),
      columnHelper.accessor('status_text', {
        header: () => <span>Status</span>,
        cell: (info) => {
          const value = info.getValue()
          const statusId = info.row.original.status
          const badgeColor = badgeColorByStatusId(statusId) || badgeColorByStatusText(value)
          const label = t(`${value}`)
          return <span className={`badge ${badgeColor}`}>{label}</span>
        },
      }),
      columnHelper.accessor('id', {
        id: 'actions',
        header: () => t('order-aksi'),
        cell: (info) => {
          return (
            <TableUI.ActionButtons>
              <ActionIcon
                title='Detail'
                iconPath='/media/icons/duotune/file-text-fill.svg'
                onClick={() => onClickDetail(info.row.original)}
              />
              <ActionIcon
                title={t('order-lampiran')}
                iconPath='/media/icons/duotune/archive-fill.svg'
                onClick={() => onClickAttachment(info.row.original)}
              />
              <ActionIcon
                title={t('Comment')}
                iconPath='/media/icons/duotune/chat-left-text-fill.svg'
                onClick={() => onClickComment(info.row.original)}
              />
            </TableUI.ActionButtons>
          )
        },
      }),
    ],
    []
  )

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    debugTable: true,
  })

  function handlePageSizeChange(value: number | undefined | null) {
    dispatch(setFilterContracts({per_page: value ?? 5}))
  }

  function handlePageChange(page: number | null) {
    if (!page) return
    dispatch(setFilterContracts(filter))
  }

  function tdClass(order: IOrder) {
    const isSalesAnper = roleUser === 'salesAnper'
    const isYellow = (order.status === 2 || order.is_amendment) && isSalesAnper
    return isYellow ? 'bg-kuning' : ''
  }

  return (
    <TableUI.Container>
      <TableUI.Header>
        <SelectPageSize
          initialValue={filter?.per_page ?? 5}
          onChange={(value: any) => handlePageSizeChange(value?.value)}
        />
        <TableUI.HeaderRight>{headerContent}</TableUI.HeaderRight>
      </TableUI.Header>

      {isLoading ? (
        <TableLoader />
      ) : !data ? (
        <TableEmptyData onReloadTable={() => null} />
      ) : (
        <>
          <TableUI.TableWrapper>
            <TableUI.Table>
              <TableUI.THead>
                {/* row filter */}
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </TableUI.THead>
              <TableUI.TBody>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} style={{background: 'white'}}>
                    {headerGroup.headers.map((header) => {
                      const hideColumn = header.isPlaceholder || header.column.id === 'actions'
                      return (
                        <td key={header.id} style={{padding: '0.5rem'}}>
                          {hideColumn ? null : header.column.getCanFilter() ? (
                            <Filter column={header.column} table={table} />
                          ) : null}
                        </td>
                      )
                    })}
                  </tr>
                ))}

                {/* row data */}
                {table.getRowModel().rows.map((row) => {
                  return (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className={tdClass(row.original)}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  )
                })}
              </TableUI.TBody>
            </TableUI.Table>
          </TableUI.TableWrapper>
          {data && data?.data && (
            <PaginationTable
              links={data?.data?.links}
              firstPageUrl={data?.data?.first_page_url}
              lastPageUrl={data?.data?.last_page_url}
              onPageChange={(page) => handlePageChange(Number(page))}
            />
          )}
        </>
      )}
    </TableUI.Container>
  )
}
